import { getListOfPositionsForAssignment, getEmployeeVacancyForPeriod } from "@/api";

const VACANCY_TOLERANCE = 0.0001;
const PAGE_SIZE = 20;
const TARGET_MATCHES = 10;
const MAX_REQUESTS_WITH_MATCH = 10;
const MAX_REQUESTS = 50;

const toNumber = (value) => {
  const number = Number(value);
  return value === null || value === undefined || !Number.isFinite(number) ? null : number;
};

/** Both vacancies come from the backend payload; only a positive, equal pair matches. */
export function vacanciesMatch(positionVacancy, employeeVacancy) {
  const position = toNumber(positionVacancy);
  const employee = toNumber(employeeVacancy);
  if (position === null || employee === null || employee <= 0) return false;
  return Math.abs(position - employee) < VACANCY_TOLERANCE;
}

export function formatFte(value) {
  const number = toNumber(value);
  return number === null ? "" : `${number} FTE`;
}

export function noMatchMessage(employeeVacancy) {
  const fte = formatFte(employeeVacancy);
  return fte
    ? `No positions match this employee's availability (${fte})`
    : "No positions match this employee's availability";
}

/** Row data for PositionPickerOption: capacity and vacancy are those of the chosen period. */
export function mapPositionForPrimaryAssignment(item) {
  const codeDigits = item.code_str?.match(/\d+/);
  return {
    id: item.id,
    code: codeDigits ? codeDigits[0] : item.id,
    code_str: item.code_str,
    name: item.name,
    status: item.status,
    isInvalid: Boolean(item.is_invalid),
    directResponsibilitiesCount: item.direct_responsibilities_count || 0,
    notActiveDirectResponsibilitiesCount: item.not_active_direct_responsibilities_count || 0,
    validRolesCount: item.valid_roles_count || 0,
    invalidRolesCount: item.invalid_roles_count || 0,
    validResponsibilitiesCount: item.active_responsibilities_count || 0,
    capacity: toNumber(item.capacity) ?? 0,
    vacancy: toNumber(item.vacancy),
    allocation: item.allocation === 0 || !item.allocation ? "not" : String(item.allocation),
    archived: item.archived || false,
    invalidityReason: item.invalidity_reason || "",
    assigned_primary: item.assigned_primary || null,
    occupied_by: item.occupied_by || null,
    primary_occupied_by: item.primary_occupied_by || null,
    disabled: false,
  };
}

/**
 * The employee's vacancy for the period starting at `effectiveFrom`, read from
 * the backend payload. Falls back to the employee's availability when the
 * payload carries no vacancy.
 */
export async function fetchEmployeeVacancy({ accountId, employee, effectiveFrom, effectiveTo }) {
  try {
    const { data } = await getEmployeeVacancyForPeriod({
      account_id: accountId,
      employeeId: employee.id,
      effectiveFrom,
      effectiveTo,
    });
    const vacancy = toNumber(data?.vacancy);
    if (vacancy !== null) return vacancy;
  } catch (error) {
    console.error("Employee vacancy error:", error);
  }
  return toNumber(employee.availability);
}

/**
 * One page of positions whose vacancy for the chosen date equals the
 * employee's. The backend has no equal-vacancy filter, so server pages are
 * walked from `offset` until enough rows match or the list is exhausted.
 */
export async function loadEqualVacancyPositions({
  accountId,
  name = "",
  offset = 0,
  effectiveFrom,
  effectiveTo,
  employeeVacancy,
  excludeCodeStrs = [],
}) {
  const items = [];
  let nextOffset = offset;
  let totalCount = 0;
  for (let request = 0; request < MAX_REQUESTS && items.length < TARGET_MATCHES; request++) {
    if (request >= MAX_REQUESTS_WITH_MATCH && items.length > 0) break;
    const { data } = await getListOfPositionsForAssignment({
      accountId,
      limit: PAGE_SIZE,
      offset: nextOffset,
      name,
      effectiveFrom,
      effectiveTo,
    });
    totalCount = data.count || 0;
    const results = data.results || [];
    nextOffset += results.length;
    results.forEach((position) => {
      if (
        position.status === "active" &&
        vacanciesMatch(position.vacancy, employeeVacancy) &&
        !excludeCodeStrs.includes(position.code_str)
      ) {
        items.push(mapPositionForPrimaryAssignment(position));
      }
    });
    if (results.length === 0 || nextOffset >= totalCount) break;
  }
  return { items, nextOffset, hasMore: nextOffset < totalCount, totalCount };
}

const REJECTION_FALLBACK = "The position could not be assigned. Please try again.";

const firstMessage = (value) => {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    return value.map(firstMessage).find(Boolean) || "";
  }
  if (value && typeof value === "object") {
    return (
      Object.keys(value)
        .filter((key) => key !== "code")
        .map((key) => firstMessage(value[key]))
        .find(Boolean) || ""
    );
  }
  return "";
};

/** The backend's own rejection text (vacancy mismatch, overlap, occupied) for a 4xx failure. */
export function getAssignmentRejectionMessage(error) {
  const response = error?.response;
  if (!response || !(response.status < 500)) return REJECTION_FALLBACK;
  return firstMessage(response.data) || REJECTION_FALLBACK;
}
